/**
 * R81 · Distribution channels registry + picker.
 * ----------------------------------------------------------------
 * A stable list of channel keys with icon + label + colour used
 * everywhere a line row surfaces "where this deliverable lives" —
 * table column, package preview, copy/print output, shared view.
 *
 * R82 · Uses `react-icons/si` (Simple Icons — the actual filled brand
 * logos) so the strip reads instantly and prints cleanly. Generic
 * non-brand channels (Website, Email, Doc, Newsletter, Deck) use
 * Font Awesome's solid set for the same filled-glyph look.
 */
import { useState } from "react";
import {
  SiInstagram, SiFacebook, SiX, SiYoutube, SiTiktok,
  SiWhatsapp, SiTelegram, SiGoogle, SiPinterest, SiSubstack, SiThreads,
} from "react-icons/si";
import {
  FaGlobe, FaEnvelope, FaNewspaper, FaFilePdf, FaFileAlt, FaBlog, FaPlus,
  FaLinkedin,
} from "react-icons/fa";

export const CHANNELS = [
  { key: "instagram", label: "Instagram", icon: SiInstagram, color: "#E1306C" },
  { key: "facebook",  label: "Facebook",  icon: SiFacebook,  color: "#1877F2" },
  { key: "linkedin",  label: "LinkedIn",  icon: FaLinkedin,  color: "#0A66C2" },
  { key: "twitter",   label: "X / Twitter", icon: SiX,       color: "#000000" },
  { key: "threads",   label: "Threads",   icon: SiThreads,   color: "#000000" },
  { key: "youtube",   label: "YouTube",   icon: SiYoutube,   color: "#FF0000" },
  { key: "tiktok",    label: "TikTok",    icon: SiTiktok,    color: "#010101" },
  { key: "pinterest", label: "Pinterest", icon: SiPinterest, color: "#E60023" },
  { key: "whatsapp",  label: "WhatsApp",  icon: SiWhatsapp,  color: "#25D366" },
  { key: "telegram",  label: "Telegram",  icon: SiTelegram,  color: "#26A5E4" },
  { key: "google_business", label: "Google Business", icon: SiGoogle, color: "#4285F4" },
  { key: "website",   label: "Website",   icon: FaGlobe,     color: "#0EA5E9" },
  { key: "blog",      label: "Blog",      icon: FaBlog,      color: "#F97316" },
  { key: "email",     label: "Email",     icon: FaEnvelope,  color: "#6B7280" },
  { key: "newsletter",label: "Newsletter", icon: FaNewspaper, color: "#7C3AED" },
  { key: "substack",  label: "Substack",  icon: SiSubstack,  color: "#FF6719" },
  { key: "deck",      label: "Deck / PDF", icon: FaFilePdf,   color: "#DC2626" },
  { key: "doc",       label: "Document",  icon: FaFileAlt,  color: "#334155" },
];

const CHANNEL_MAP = new Map(CHANNELS.map((c) => [c.key, c]));
export const getChannel = (key) => CHANNEL_MAP.get(key);

/**
 * Inline read-only strip of channel icons for a row. Falls back to a dash
 * if the array is empty. Brand glyphs are filled SVG paths so they stay
 * crisp when printed.
 *
 * Nov 2026 · Switched from bare SVGs to tinted chips — each channel
 * icon sits inside a 16×16 rounded square filled with the channel's
 * brand colour at 12% opacity, with the icon itself in the full brand
 * tone. Reads as a lightweight colour-grouped strip at a glance.
 */
export function ChannelIcons({ channels, size = 11, testid }) {
  if (!channels || !channels.length) {
    return <span className="text-muted-foreground text-[10px]" data-testid={testid}>—</span>;
  }
  return (
    <span className="inline-flex items-center gap-0.5 flex-wrap" data-testid={testid}>
      {channels.map((k) => {
        const c = getChannel(k);
        if (!c) return null;
        const Icon = c.icon;
        return (
          <span
            key={k}
            title={c.label}
            className="inline-flex items-center justify-center w-4 h-4 rounded-[3px]"
            style={{ backgroundColor: `${c.color}1F`, color: c.color }}
            data-testid={testid ? `${testid}-${k}` : undefined}
          >
            <Icon size={size} />
          </span>
        );
      })}
    </span>
  );
}

/**
 * Compact popover picker that toggles channel membership.
 *
 * Nov 2026 · Selected channels float to the top of the list so the
 * current selection is always visible at a glance, and each row's
 * icon sits in a brand-coloured chip for faster scanning.
 */
export function ChannelPicker({ value = [], onChange, testid = "channel-picker" }) {
  const [open, setOpen] = useState(false);
  const has = new Set(value || []);
  const toggle = (k) => {
    if (has.has(k)) onChange(value.filter((x) => x !== k));
    else onChange([...(value || []), k]);
  };
  // Nov 2026 · Order the picker so every currently-selected channel
  // comes first (preserving the user's selection order), then the
  // remaining channels in registry order. Keeps the chip strip and
  // the dropdown visually aligned, and makes "what's on" obvious.
  const orderedChannels = [
    ...(value || []).map((k) => getChannel(k)).filter(Boolean),
    ...CHANNELS.filter((c) => !has.has(c.key)),
  ];
  return (
    <div className="relative inline-flex items-center gap-1">
      <ChannelIcons channels={value} testid={`${testid}-icons`} size={11} />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-muted-foreground/70 hover:text-foreground border border-transparent hover:border-border rounded-none w-4 h-4 inline-flex items-center justify-center"
        title="Assign distribution channels"
        data-testid={testid}
      >
        <FaPlus size={9} />
      </button>
      {open && (
        <div
          className="absolute z-50 top-5 right-0 bg-card border border-border shadow-md p-1.5 w-56 max-h-72 overflow-y-auto"
          onMouseLeave={() => setOpen(false)}
          data-testid={`${testid}-popover`}
        >
          {value && value.length > 0 && (
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground px-2 py-0.5">
              Selected
            </div>
          )}
          {orderedChannels.map((c, idx) => {
            const on = has.has(c.key);
            const Icon = c.icon;
            // Insert the "available" divider right after the last selected row.
            const showDivider = value && value.length > 0 && idx === value.length;
            return (
              <div key={c.key}>
                {showDivider && (
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground px-2 py-0.5 border-t border-border mt-1 pt-1">
                    Available
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggle(c.key)}
                  className={`w-full flex items-center gap-2 px-2 py-1 text-[11px] rounded-none ${on ? "bg-primary/5" : "hover:bg-muted/60"}`}
                  data-testid={`${testid}-opt-${c.key}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-[3px] shrink-0"
                    style={{ backgroundColor: `${c.color}1F`, color: c.color }}
                  >
                    <Icon size={11} />
                  </span>
                  <span className="flex-1 text-left">{c.label}</span>
                  {on && <span className="text-[10px] text-primary">✓</span>}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Plain-text join of channel labels (used for TSV clipboard output).
 */
export function channelsToPlainText(channels) {
  if (!channels || !channels.length) return "";
  return channels.map((k) => getChannel(k)?.label || k).join(", ");
}
